import { useEffect, useRef } from 'react';

const PREFIX = 'lb-scroll:';

function read(key: string): number | null {
  try {
    const raw = sessionStorage.getItem(`${PREFIX}${key}`);
    if (raw == null) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function write(key: string, top: number): void {
  try {
    sessionStorage.setItem(`${PREFIX}${key}`, String(top));
  } catch {
    // storage unavailable — ignore, never block navigation
  }
}

/**
 * Remembers the window scroll position for a list key and restores it the first
 * time the list becomes ready after a remount. Used so returning from a detail
 * page lands the user back where they left off, in the same tab.
 */
export function useListScrollMemory(key: string, ready: boolean): void {
  const restored = useRef(false);

  // Save the current position on unmount and on tab switches.
  useEffect(() => {
    const save = () => write(key, window.scrollY);
    window.addEventListener('pagehide', save);
    return () => {
      window.removeEventListener('pagehide', save);
      save();
    };
  }, [key]);

  // Restore once, right after the list has rendered its rows.
  useEffect(() => {
    if (!ready || restored.current) return;
    restored.current = true;
    const top = read(key);
    if (top == null) return;
    requestAnimationFrame(() => window.scrollTo({ top, behavior: 'auto' }));
  }, [key, ready]);
}